import {
  convertArea as turfConvertArea,
  convertLength,
  type Units,
} from '@turf/helpers';
import type { Feature, LineString, Polygon } from 'geojson';

import {
  DrawingMode,
  Interaction,
  isRegion,
  measureArea,
  measureDistance,
  measureRadius,
} from '../drawing/geometry';

export type DistanceUnit = Extract<
  Units,
  'miles' | 'feet' | 'yards' | 'meters'
>;
export type AreaUnit = DistanceUnit | 'acres';

export const convertDistance = (km: number, unit: DistanceUnit): number =>
  convertLength(km, 'kilometers', unit);

export const convertArea = (m2: number, unit: AreaUnit): number =>
  turfConvertArea(m2, 'meters', unit);

export const DISTANCE_UNIT_LABELS: Record<DistanceUnit, string> = {
  miles: 'Miles',
  feet: 'Feet',
  yards: 'Yards',
  meters: 'Meters',
};

export const AREA_UNIT_LABELS: Record<AreaUnit, string> = {
  miles: 'Sq Miles',
  feet: 'Sq Feet',
  yards: 'Sq Yards',
  acres: 'Acres',
  meters: 'Sq Meters',
};

const DISTANCE_UNITS: DistanceUnit[] = ['miles', 'feet', 'yards', 'meters'];
const AREA_UNITS: AreaUnit[] = ['miles', 'feet', 'yards', 'acres', 'meters'];

export const DISTANCE_OPTIONS: Array<{ value: DistanceUnit; label: string }> =
  DISTANCE_UNITS.map(value => ({ value, label: DISTANCE_UNIT_LABELS[value] }));

export const AREA_OPTIONS: Array<{ value: AreaUnit; label: string }> =
  AREA_UNITS.map(value => ({ value, label: AREA_UNIT_LABELS[value] }));

export type EffectiveShape = 'linestring' | 'polygon' | 'circle' | null;

type MeasurableDrawingMode = Exclude<DrawingMode, 'point'>;

const isMeasurableDrawingMode = (
  mode: Interaction,
): mode is MeasurableDrawingMode =>
  mode === 'circle' || mode === 'polygon' || mode === 'linestring';

export const getEffectiveShape = (
  feature: Feature | null,
  mode: Interaction,
): EffectiveShape => {
  if (feature === null) {
    return isMeasurableDrawingMode(mode) ? mode : null;
  }

  switch (feature.geometry.type) {
    case 'LineString':
      return 'linestring';
    case 'Polygon':
      return feature.properties?.mode === 'circle' ? 'circle' : 'polygon';
    default:
      return null;
  }
};

export const SHAPE_LABELS: Record<Exclude<EffectiveShape, null>, string> = {
  linestring: 'Measure',
  polygon: 'Draw Polygon Area',
  circle: 'Draw Radius Area',
};

export type MessageTone = 'green' | 'orange';
export type MessageType = 'measure' | 'polygon' | 'noItem' | 'zoom' | 'none';
export type MessageDef = { text: string; tone?: MessageTone };

export const MESSAGE_DEFS: Record<MessageType, MessageDef> = {
  measure: { text: 'Click points on the map to measure distance.' },
  polygon: { text: 'Draw an area now.', tone: 'green' },
  noItem: { text: 'No items to show.', tone: 'orange' },
  zoom: {
    text: 'Please zoom closer on the map to view items.',
    tone: 'orange',
  },
  none: { text: '' },
};

export type MapItemCounts = {
  total: number;
  clusters: number;
  assets: number;
  workOrders: number;
  users: number;
};

export const getMessageType = ({
  showLineString,
  hasActiveFeature,
  itemCounts,
}: {
  showLineString: boolean;
  hasActiveFeature: boolean;
  itemCounts?: MapItemCounts;
}): MessageType => {
  switch (true) {
    case showLineString:
      return 'measure';
    case !hasActiveFeature:
      return 'polygon';
    case itemCounts?.total === 0 && itemCounts.clusters > 0:
      return 'zoom';
    case itemCounts?.total === 0:
      return 'noItem';
    default:
      return 'none';
  }
};

export type Measurements = {
  distanceKm: number;
  radiusKm: number;
  areaM2: number;
};

export const computeMeasurements = (feature: Feature | null): Measurements => {
  const isLineString = feature?.geometry.type === 'LineString';
  const isPolygon = isRegion(feature);

  const perimeterAsLine: Feature<LineString> = {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'LineString',
      coordinates: isPolygon
        ? (feature as Feature<Polygon>).geometry.coordinates[0]
        : [],
    },
  };

  const distanceKm = isLineString
    ? measureDistance(feature as Feature<LineString>)
    : isPolygon
      ? measureDistance(perimeterAsLine)
      : 0;

  const radiusKm = isPolygon ? measureRadius(feature as Feature<Polygon>) : 0;
  const areaM2 = isPolygon ? measureArea(feature as Feature<Polygon>) : 0;

  return { distanceKm, radiusKm, areaM2 };
};

export const formatNumber = (value: number): string =>
  new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 3,
    minimumFractionDigits: 3,
  }).format(value);
